import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import {
  applyColorScheme,
  readAppThemeFlags,
  enabledThemeClasses,
  APP_THEMES,
  APP_THEMES_STORAGE_KEY,
  type AppThemeFlag,
  type AppThemeFlags,
  type ColorScheme,
} from "@/lib/themes";

type Theme = "dark" | "light" | "system";
export type { Theme };
export const THEME_STORAGE_KEY = "crosbe-theme";
export const DEFAULT_THEME: Theme = "system";

export function parseStoredTheme(stored: string | null, defaultTheme: Theme = DEFAULT_THEME): Theme {
  if (stored === "light" || stored === "dark" || stored === "system") {
    return stored;
  }
  return defaultTheme;
}

export function resolveThemeMode(theme: Theme, prefersDark: boolean): boolean {
  return theme === "dark" || (theme === "system" && prefersDark);
}

const PREFERS_DARK_QUERY = "(prefers-color-scheme: dark)";

/** The OS preference right now; false where there is no window (SSR, tests without one). */
function readPrefersDark(): boolean {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") return false;
  return window.matchMedia(PREFERS_DARK_QUERY).matches;
}

interface ThemeContextType {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  /**
   * #3734 the theme the screen actually shows: "system" resolved against the
   * OS prefers-color-scheme, live. Read this, never `theme === "dark"`, to pick
   * anything by light or dark (the sidebar logo, for one).
   */
  resolvedTheme: "light" | "dark";
  colorScheme: ColorScheme;
  setColorScheme: (scheme: ColorScheme) => void;
  /** #809 app theme registry flags (default off). See APP_THEMES in lib/themes. */
  appThemes: AppThemeFlags;
  setAppTheme: (flag: AppThemeFlag, enabled: boolean) => void;
  /**
   * #831 concept-preview flag (default OFF, per-device). Gates opt-in UI pulled
   * from the design concept (notification panel, context rail) so the future
   * direction can ship continuously without changing what current users see —
   * same non-disruptive posture as the #809 theme flags, but this one gates
   * COMPONENTS (React render), not a CSS re-skin, so it lives outside APP_THEMES.
   */
  conceptPreview: boolean;
  setConceptPreview: (enabled: boolean) => void;
}

const ThemeContext = createContext<ThemeContextType>({
  theme: DEFAULT_THEME,
  setTheme: () => {},
  resolvedTheme: "light",
  colorScheme: "default",
  setColorScheme: () => {},
  appThemes: {},
  setAppTheme: () => {},
  conceptPreview: false,
  setConceptPreview: () => {},
});

/** #831 localStorage key for the concept-preview flag. */
const CONCEPT_PREVIEW_KEY = "crosbe-concept-preview";

export function useTheme() {
  return useContext(ThemeContext);
}

export function ThemeProvider({
  children,
  defaultTheme = DEFAULT_THEME,
}: {
  children: ReactNode;
  defaultTheme?: Theme;
}) {
  const [theme, setTheme] = useState<Theme>(() => {
    if (typeof window !== "undefined") {
      return parseStoredTheme(localStorage.getItem(THEME_STORAGE_KEY), defaultTheme);
    }
    return defaultTheme;
  });

  // #3734 tracked as state so a consumer re-renders when the OS flips while the
  // theme is "system". Seeded synchronously so the first paint already matches.
  const [prefersDark, setPrefersDark] = useState<boolean>(readPrefersDark);
  const resolvedTheme = resolveThemeMode(theme, prefersDark) ? "dark" : "light";

  const [colorScheme, setColorSchemeState] = useState<ColorScheme>(() => {
    if (typeof window !== "undefined") {
      return (localStorage.getItem("crosbe-color-scheme") as ColorScheme) || "default";
    }
    return "default";
  });

  const setColorScheme = (scheme: ColorScheme) => {
    setColorSchemeState(scheme);
    localStorage.setItem("crosbe-color-scheme", scheme);
  };

  // #809 app theme registry — same localStorage mechanism as the dark-mode
  // toggle above: lazy init restores instantly on load, the setter persists on
  // toggle, and consumers (Sidebar, Settings) re-render immediately via context.
  // v1 is per-device only (no server sync — ui_prefs migration is v2).
  const [appThemes, setAppThemesState] = useState<AppThemeFlags>(() =>
    readAppThemeFlags()
  );

  const setAppTheme = (flag: AppThemeFlag, enabled: boolean) => {
    setAppThemesState((prev) => {
      // Store only `true` flags — an absent key IS the default-off state, so a
      // fresh device and a toggled-off device serialise identically.
      const next: AppThemeFlags = { ...prev };
      if (enabled) next[flag] = true;
      else delete next[flag];
      try {
        localStorage.setItem(APP_THEMES_STORAGE_KEY, JSON.stringify(next));
      } catch {
        // best-effort persistence; the in-memory state still applies this session
      }
      return next;
    });
  };

  // #831 concept-preview flag — per-device, default off. Only the literal "1"
  // enables it, so any stale/garbled value reads as off (the safe default).
  const [conceptPreview, setConceptPreviewState] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    try {
      return localStorage.getItem(CONCEPT_PREVIEW_KEY) === "1";
    } catch {
      return false;
    }
  });

  const setConceptPreview = (enabled: boolean) => {
    setConceptPreviewState(enabled);
    try {
      if (enabled) localStorage.setItem(CONCEPT_PREVIEW_KEY, "1");
      else localStorage.removeItem(CONCEPT_PREVIEW_KEY);
    } catch {
      // best-effort persistence; in-memory state still applies this session
    }
  };

  useEffect(() => {
    const root = document.documentElement;
    const prefersDarkQuery = window.matchMedia(PREFERS_DARK_QUERY);

    function apply() {
      setPrefersDark(prefersDarkQuery.matches);
      const isDark = resolveThemeMode(theme, prefersDarkQuery.matches);
      root.classList.toggle("dark", isDark);
      applyColorScheme(colorScheme, isDark);
    }

    apply();
    localStorage.setItem(THEME_STORAGE_KEY, theme);

    // Listen for system theme changes when in "system" mode
    prefersDarkQuery.addEventListener("change", apply);
    return () => prefersDarkQuery.removeEventListener("change", apply);
  }, [theme, colorScheme]);

  // Root-scoped app themes (e.g. the full-surface navy) mount their class on
  // <html> so they can re-map the global --color-* tokens app-wide. Sidebar-
  // scoped themes are applied by <Sidebar> itself. Separate effect so the class
  // set tracks appThemes without re-running the dark-mode listener.
  useEffect(() => {
    const root = document.documentElement;
    const enabled = enabledThemeClasses(appThemes, "root").split(" ").filter(Boolean);
    for (const t of APP_THEMES) {
      if (t.scope === "root") root.classList.remove(t.className);
    }
    for (const cls of enabled) root.classList.add(cls);
    return () => {
      for (const cls of enabled) root.classList.remove(cls);
    };
  }, [appThemes]);

  return (
    <ThemeContext.Provider
      value={{ theme, setTheme, resolvedTheme, colorScheme, setColorScheme, appThemes, setAppTheme, conceptPreview, setConceptPreview }}
    >
      {children}
    </ThemeContext.Provider>
  );
}
